import { Box, Typography } from '@mui/material';
import styles from './Lineup.module.css';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import Porcentajes from '../complementos/Porcentajes';
import LineupPlayerAlternatives from './LineupPlayerAlternatives';
import TshirtIconComponent from '@/components/page-components/fixtures/fixture-player-stadium-player/tshirt-icon-component';
import JugadorEventCard from '../Jugadores/JugadorEventCard';
import PuntuacionConfirmada from '../complementos/PuntuacionConfirmada';
import { getTopForPlayer } from '@/helpers/analisis-plantilla-fantasy-helper';
import useFormationName from '../hooks/useFormationName';
import useShowPlayerAlineacionProbable from '../hooks/useShowPlayerAlineacionProbable';
import CountryConvocado from '../complementos/CountryConvocado';
import Apercibidos from '../complementos/Apercibidos';
import useStatusIcon from '@/DesignSystem/hooks/useStatusIcon';
import TeamStatsFloatingMenu from './TeamStatsFloatingMenu';
import ZoomControls from '../complementos/ZoomControls';
import LottieIconComponent, { LOTTIE_ICONS } from '@/DesignSystem/features/lottie/LottieIconComponent';
import useIconoEstadoDelTiempo from '../hooks/useIconoEstadoDelTiempo';

const LineupSplitPlayerCard = memo(
    ({ player, isMobile, players, mode, events, estadisticasPartido, cardSize, ...props }) => {
        const width = cardSize;

        const fontSize = useMemo(() => {
            if (isMobile) {
                return 10;
            }
            return 12;
        }, [isMobile]);

        const playerEvents = useMemo(() => {
            return events?.filter(
                (event) =>
                    (event.player.id === player.id && event.player.id) ||
                    (event.assist.id === player.id && event.assist.id)
            );
        }, [events, player.id]);

        const estadisticaPartido = estadisticasPartido?.estadisticas?.[player?.id];
        const handleShowPlayerAlineacionProbable = useShowPlayerAlineacionProbable(player);

        const statusIcon = useStatusIcon({
            status: player?.status,
            statusLevel: player?.statusLevel,
            width: 14,
            height: 14,
            skipIfNotOk: true,
        });

        const positionSubstEvent = useMemo(() => {
            if (width < 768) {
                return {
                    top: 0,
                    right: -10,
                };
            }
            return {
                top: 5,
                right: -5,
            };
        }, [width]);

        return (
            <Box
                className={styles.estadioLinePlayer}
                sx={{ width: width * 1.1, cursor: 'pointer' }}
                onClick={handleShowPlayerAlineacionProbable}
                {...props}
            >
                <Box position={'relative'}>
                    {player.image && (
                        <Box position={'relative'} width={width} height={width} sx={{ margin: '0 auto' }}>
                            <CustomImageComponent
                                src={player.image}
                                alt={player.name}
                                width={width}
                                height={width}
                                style={{ borderRadius: '50%' }}
                            />

                            <Box sx={{ position: 'absolute', bottom: -2, left: 0 }}>{statusIcon}</Box>
                            <CountryConvocado
                                country={player.countryConvocado}
                                isConvocado={player.isConvocado}
                                position={'absolute'}
                                top={5}
                                left={5}
                            />

                            {player.isBufon && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '5px',
                                        right: '25px',
                                    }}
                                >
                                    <LottieIconComponent icon={LOTTIE_ICONS.CLOWN} width={30} height={30} />
                                </Box>
                            )}

                            <Apercibidos
                                cards={player.cards}
                                size={12}
                                color={'#fddf00'}
                                position={'absolute'}
                                bottom={0}
                                left={'0px'}
                            />
                        </Box>
                    )}
                    {!player.image && (
                        <TshirtIconComponent
                            primary={player.primary}
                            colorNumber={player.colorNumber}
                            number={player.number}
                            width={width * 0.8}
                            height={width * 0.8}
                        />
                    )}

                    {(mode === 'confirmed' || mode === 'last') && (
                        <PuntuacionConfirmada
                            estadisticaPartido={estadisticaPartido}
                            width="30px"
                            margin="5px auto 0 auto"
                            position="absolute"
                            bottom={0}
                            right={0}
                        />
                    )}

                    {mode === 'predict' && (
                        <Porcentajes
                            porcentaje={player.chance}
                            fontSize={10}
                            display={'flex'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            position={'absolute'}
                            bottom={0}
                            right={0}
                            width={'30px'}
                            height={'15px'}
                            comentario={player.comentario}
                        />
                    )}
                    {playerEvents?.length > 0 && (
                        <Box
                            position={'absolute'}
                            top={-10}
                            left={0}
                            width={'100%'}
                            display={'flex'}
                            alignItems={'center'}
                        >
                            {playerEvents.map((event) => (
                                <JugadorEventCard
                                    key={event.id}
                                    event={event}
                                    player={player}
                                    direction={'row'}
                                    includeTime={true}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                <Typography
                    fontSize={fontSize}
                    variant="body1"
                    color="#fff"
                    noWrap
                    sx={{ width: width * 1.2 }}
                    textAlign={'center'}
                >
                    {player.name}
                </Typography>

                <LineupPlayerAlternatives player={player} players={players} fontSize={fontSize} />
            </Box>
        );
    }
);

LineupSplitPlayerCard.displayName = 'LineupSplitPlayerCard';

const LineupSplit = ({
    home = [],
    away = [],
    prediccionTiempo,
    weatherIconPosition = 'right',
    mode,
    events,
    estadisticasPartido,
    homeFormation,
    awayFormation,
    isMobile,
    homeId,
    awayId,
}) => {
    const [height, setHeight] = useState(0);
    const [teamHeight, setTeamHeight] = useState(0);
    const [cardSize, setCardSize] = useState(60);

    const boxRef = useRef(null);

    // ✅ Hook llamado al nivel del componente, no en JSX
    const tiempo = useIconoEstadoDelTiempo(prediccionTiempo, weatherIconPosition);

    const handleZoomIn = () => {
        setCardSize((prev) => Math.min(prev + 5, 90));
    };

    const handleZoomOut = () => {
        setCardSize((prev) => Math.max(prev - 5, 30));
    };

    useEffect(() => {
        const updateHeight = () => {
            if (boxRef.current) {
                const width = boxRef.current.offsetWidth;
                const height = width < 100 ? 650 : 950;
                setHeight(`${height}px`);
                setTeamHeight(`${height / 2}px`);
            }
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    // Lógica dinámica basada en formación para home
    const homeFormationClean = useMemo(() => {
        if (!homeFormation) return null;
        return homeFormation.replace('alineacion_', '');
    }, [homeFormation]);

    const homeLines = useMemo(() => {
        if (!homeFormationClean || !home?.length) return [];

        const parts = homeFormationClean.split('-').map(Number);
        const playersExcludingGoalkeeper = home
            .filter((player) => player.grid !== 11 && player.grid > 0)
            .sort((a, b) => a.grid - b.grid);

        let currentIndex = 0;
        const result = parts.map((count) => {
            const group = playersExcludingGoalkeeper.slice(currentIndex, currentIndex + count);
            currentIndex += count;
            return group;
        });

        return result;
    }, [homeFormationClean, home]);

    const homePortero = useMemo(() => {
        return home?.find((player) => player.grid === 11);
    }, [home]);

    // Lógica dinámica basada en formación para away
    const awayFormationClean = useMemo(() => {
        if (!awayFormation) return null;
        return awayFormation.replace('alineacion_', '');
    }, [awayFormation]);

    const awayLines = useMemo(() => {
        if (!awayFormationClean || !away?.length) return [];

        const parts = awayFormationClean.split('-').map(Number);
        const playersExcludingGoalkeeper = away
            .filter((player) => player.grid !== 11 && player.grid > 0)
            .sort((a, b) => a.grid - b.grid);

        let currentIndex = 0;
        const result = parts.map((count) => {
            const group = playersExcludingGoalkeeper.slice(currentIndex, currentIndex + count);
            currentIndex += count;
            return group;
        });

        return result;
    }, [awayFormationClean, away]);

    const awayPortero = useMemo(() => {
        return away?.find((player) => player.grid === 11);
    }, [away]);

    const getGap = (linea, cantidad) => {
        if (linea === 0) {
            switch (cantidad) {
                case 3:
                    return '6%';
                case 4:
                    return '10%';
                case 5:
                    return '8%';
                case 6:
                    return '4%';
                default:
                    return 0;
            }
        } else if (linea === 1) {
            switch (cantidad) {
                case 2:
                case 3:
                    return '5%';
                case 4:
                    return '10%';
                case 5:
                    return '10%';
                default:
                    return 0;
            }
        } else if (linea === 2) {
            switch (cantidad) {
                case 2:
                    return '12%';
                case 3:
                    return '20%';
                default:
                    return '5%';
            }
        } else if (linea === 3) {
            return '6%';
        }

        return 0;
    };

    const renderLine = (players, linea, team, formation, isHome) => {
        if (!players?.length) return null;
        return (
            <Box className={styles.estadioLineSplit} sx={{ gap: getGap(linea, players.length) }}>
                {players
                    ?.sort((a, b) => a.grid - b.grid)
                    ?.map((player, index) => {
                        const top = getTopForPlayer(formation, player.grid);
                        const styles = !isHome ? { top: top } : { bottom: top };

                        return (
                            <LineupSplitPlayerCard
                                player={player}
                                key={`${player.id}-${index}`}
                                isMobile={isMobile}
                                players={team}
                                mode={mode}
                                events={events}
                                estadisticasPartido={estadisticasPartido}
                                cardSize={cardSize}
                                {...styles}
                            />
                        );
                    })}
            </Box>
        );
    };

    return (
        <Box
            ref={boxRef}
            className={styles.estadioSplit}
            sx={{
                width: '100%',
                height: height,
                transition: 'height 0.2s ease',
                position: 'relative',
            }}
        >
            {homeFormation && (
                <Typography
                    color="rgba(255, 255, 255, 0.5)"
                    sx={{
                        position: 'absolute',
                        top: { xs: 0, md: 0 },
                        left: { xs: 12, md: 16 },
                        zIndex: 1000,
                        fontSize: 10,
                    }}
                >
                    Formación: {useFormationName(homeFormation)}
                </Typography>
            )}

            {awayFormation && (
                <Typography
                    color="rgba(255, 255, 255, 0.5)"
                    sx={{
                        position: 'absolute',
                        bottom: { xs: 0, md: 0 },
                        left: { xs: 12, md: 16 },
                        zIndex: 1000,
                        fontSize: 10,
                    }}
                >
                    Formación: {useFormationName(awayFormation)}
                </Typography>
            )}

            <Box className={styles.estadioSplitEquipo} sx={{ height: teamHeight, paddingBottom: '8px' }}>
                {homePortero && renderLine([homePortero], 0, home, homeFormation, true)}
                {homeLines.map((linePlayers, idx) => {
                    return (
                        <Box key={idx} width="100%">
                            {renderLine(linePlayers, idx + 1, home, homeFormation, true)}
                        </Box>
                    );
                })}
            </Box>
            <Box className={styles.estadioSplitEquipo} sx={{ height: teamHeight, paddingBottom: '8px' }}>
                {awayLines
                    .slice()
                    .reverse()
                    .map((linePlayers, idx) => {
                        const originalIdx = awayLines.length - 1 - idx;
                        return (
                            <Box key={originalIdx} width="100%">
                                {renderLine(linePlayers, originalIdx + 1, away, awayFormation, false)}
                            </Box>
                        );
                    })}
                {awayPortero && renderLine([awayPortero], 0, away, awayFormation, false)}
            </Box>
            {tiempo}
            {home?.length > 0 && homeId > 0 && (
                <TeamStatsFloatingMenu teamId={homeId} top="20px" left="15px" />
            )}
            {away?.length > 0 && awayId > 0 && (
                <TeamStatsFloatingMenu teamId={awayId} bottom="20px" left="15px" />
            )}

            <ZoomControls
                cardSize={cardSize}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                minSize={30}
                maxSize={90}
            />
        </Box>
    );
};

export default LineupSplit;
